import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://battlebornppf.com/" },
    { "@type": "ListItem", position: 2, name: "Resources", item: "https://battlebornppf.com/resources" },
    { "@type": "ListItem", position: 3, name: "PPF for Leased Cars", item: "https://battlebornppf.com/resources/ppf-worth-it-leased-car-las-vegas" },
  ],
};

const costData = [
  { scenario: "Front bumper repaint (lease charge)", cost: "$900 – $1,800" },
  { scenario: "Hood repaint (lease charge)", cost: "$1,200 – $2,500" },
  { scenario: "Mirror replacement, one side (lease charge)", cost: "$300 – $600" },
  { scenario: "Multiple rock chip repair per panel (lease charge)", cost: "$150 – $400" },
  { scenario: "Partial front PPF (prevents all above)", cost: "From $900" },
  { scenario: "Full front-end PPF (all high-risk zones)", cost: "From $1,500" },
];

const reasons = [
  {
    title: "Rock chips are the #1 excess wear charge.",
    text: "A single I-15 drive can deposit multiple chips on an unprotected hood. PPF absorbs every impact — the paint underneath stays untouched.",
  },
  {
    title: "PPF is removable without paint damage.",
    text: "When the lease ends, XPEL PPF can be removed leaving factory paint in original condition. The dealership inspects the paint, not the film.",
  },
  {
    title: "Las Vegas UV degrades unprotected paint fast.",
    text: "UV oxidation causes dullness and clear coat hazing that triggers cosmetic wear charges. XPEL's UV-blocking top coat preserves gloss through a full lease term.",
  },
  {
    title: "It protects buyout value.",
    text: "Many Las Vegas drivers buy out their leases. PPF-protected paint is in significantly better condition, reducing reconditioning costs and preserving resale value.",
  },
  {
    title: "It costs less than a single repair charge.",
    text: "Even one chipped bumper at lease return likely exceeds the cost of a partial front PPF package. The protection pays for itself if it prevents even one damage line item.",
  },
];

const PpfLeasedCar = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Is PPF Worth It for a Leased Car in Las Vegas? Yes — Here's Why"
        description="PPF on a leased car prevents excess wear charges at return. Real cost comparison: PPF vs typical Las Vegas lease-return damage fees."
        canonical="https://battlebornppf.com/resources/ppf-worth-it-leased-car-las-vegas"
        schema={breadcrumbSchema}
        publishedDate="2025-10-01"
        modifiedDate="2026-01-15"
      />

      <article className="py-16 px-4">
        <div className="max-w-3xl mx-auto space-y-12">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-accent transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/resources" className="hover:text-accent transition-colors">Resources</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-foreground">PPF for Leased Cars</span>
          </nav>

          {/* Header */}
          <header>
            <h1 className="text-3xl md:text-4xl font-heading font-bold">
              Is PPF Worth It for a Leased Car in Las Vegas?
            </h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2 mb-8">
              By Battle Born Protection Films Team · Published Oct 2025 · Updated Jan 2026
            </div>
            <p className="text-lg font-medium leading-relaxed">
              PPF is worth it on a leased car in Las Vegas. Dealerships charge $300–$2,500 per damaged panel at lease return, and Las Vegas highways generate rock chips faster than most U.S. cities. A front-end PPF package costing $900–$1,500 typically prevents far more in end-of-lease wear charges than it costs.
            </p>
          </header>

          {/* How Lease Return Damage Charges Work */}
          <section>
            <h2 className="text-2xl font-heading font-semibold mb-4">How Lease Return Damage Charges Work</h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              When a leased vehicle is returned, the dealership inspects it under Automotive Lease Guide wear-and-use standards. Normal wear is defined narrowly: small door dings under 2 inches, very minor scuffs, and light tire wear are typically accepted. Rock chips — especially multiple chips on the hood or bumper — almost always fall outside normal wear and trigger repair charges. In Las Vegas, where I-15 and US-93 carry heavy commercial traffic through open desert, a leased vehicle accumulates hood and bumper rock chips faster than vehicles in cities with natural windbreaks.
            </p>
          </section>

          {/* Real Cost Comparison */}
          <section>
            <h2 className="text-2xl font-heading font-semibold mb-4">Real Cost Comparison</h2>
            <div className="rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Scenario</TableHead>
                    <TableHead className="font-semibold">Typical Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costData.map((row) => (
                    <TableRow key={row.scenario}>
                      <TableCell className="font-medium">{row.scenario}</TableCell>
                      <TableCell className="text-muted-foreground">{row.cost}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-base leading-relaxed text-muted-foreground mt-6">
              A common Las Vegas lease-return scenario: two hood chips, a chipped bumper, and a scratched mirror. Total dealer charge: $1,400–$3,200. A full front-end PPF package applied at lease start: $1,500. The math is straightforward — PPF also makes the car easier to maintain and prevents the UV fading that triggers cosmetic wear assessments.
            </p>
          </section>

          {/* 5 Reasons PPF Pays for Itself */}
          <section>
            <h2 className="text-2xl font-heading font-semibold mb-6">5 Reasons PPF Pays for Itself on a Lease</h2>
            <ol className="space-y-6">
              {reasons.map((reason, index) => (
                <li key={index} className="flex gap-4">
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 text-accent font-bold text-lg flex items-center justify-center">
                    {index + 1}
                  </span>
                  <div>
                    <p className="font-bold mb-1">{reason.title}</p>
                    <p className="text-muted-foreground leading-relaxed">{reason.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* What Coverage Makes Sense */}
          <section>
            <h2 className="text-2xl font-heading font-semibold mb-4">What Coverage Makes Sense for a Lease?</h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              For most leased vehicles in Las Vegas, a partial or full front-end PPF package covers the highest-risk areas: hood (full), front bumper (full), headlights and fog lights, side mirrors, and front fenders to the door opening. For vehicles parked in open lots or tight garages, adding door edge guards and handle cup protection is also worthwhile. Contact Battle Born Protection Films at{" "}
              <a href="tel:+17025599365" className="text-accent font-semibold hover:underline">(702) 559-9365</a>{" "}
              for a lease-specific consultation.
            </p>
          </section>

          {/* CTA */}
          <section className="bg-accent/10 rounded-xl p-8 text-center space-y-4">
            <h2 className="text-2xl font-heading font-bold">Protect Your Lease Investment</h2>
            <p className="text-muted-foreground">
              Get a free, no-obligation PPF quote tailored to your leased vehicle.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild variant="hero" size="lg">
                <a href="tel:+17025599365">Call (702) 559-9365</a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/contact">Request a Quote</Link>
              </Button>
            </div>
          </section>
        </div>
      </article>
    </div>
  );
};

export default PpfLeasedCar;
